import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  LinearProgress,
  Avatar,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
} from "@mui/material";
import { useState, useRef, useEffect, useMemo } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { deleteCampaignAttachment, deleteStorageFile, getCampaignFiles, getCampaigns, getSignedUrlForFile, uploadCampaignAttachments } from "../api/apiService";
import { AttachFileRounded } from "@mui/icons-material";

const FILE_SIZE_IN_MB = 2; // 2MB fixed
const MAX_FILES = 1; // 1 file fixed per campaign
const MAX_FILE_SIZE = FILE_SIZE_IN_MB * 1024 * 1024; 
const ALLOWED_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export default function Attachments() {
  const clientId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("clientId")) ?? "";
    } catch {
      return "";
    }
  }, []);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropRef = useRef();

  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState("");

  /** Load campaigns data */
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!clientId) return;
      try {
        setLoading(true);
        const [page, pageSize, search] = [0, 0, ""];
        const res = await getCampaigns(clientId, page, pageSize, search);
        const normalizedRows = res.data.campaigns.map((c) => ({ ...c }));
        setCampaigns(normalizedRows);
      } catch (err) {
        setSnackbar({ open: true, message: "Not able to fetch campaign details", severity: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, [clientId]);

  /** Fetch files for selected campaign */
    useEffect(() => {
    const fetchFiles = async () => {
        if (!clientId || !selectedId) return;

        try {
        setLoading(true);
        const res = await getCampaignFiles(clientId,selectedId);

        const files = res.data.map((file) => ({
            name: file.key.split("/").pop(),
            url: file.fileUrl,
            key: file.key,
            size: file.size,
            preview: file.fileUrl.match(/\.(jpg|jpeg|png)$/) ? file.fileUrl : null,
        }));

        setAttachments(files);

        } catch (err) {
        console.error(err);
        setAttachments([]);
        } finally {
        setLoading(false);
        }
    };

    fetchFiles();
    }, [clientId, selectedId]);

  /** Process file before upload */
  const processFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      setSnackbar({ open: true, message: "Unsupported file type.", severity: "error" });
      return null;
    }
    if (file.size > MAX_FILE_SIZE) {
      setSnackbar({ open: true, message: `File is too large. Max ${FILE_SIZE_IN_MB} MB.`, severity: "error" });
      return null;
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve({
          originalFile: file,
          name: file.name,
          size: file.size,
          type: file.type,
          //base64: reader.result.split(",")[1],
          preview: file.type.startsWith("image/") ? reader.result : null,
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    await handleNewFiles(files);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    dropRef.current.style.borderColor = "grey";
    const files = Array.from(e.dataTransfer.files);
    await handleNewFiles(files);
  };

  const handleNewFiles = async (files) => {

    if (!selectedId) {
        setSnackbar({
            open: true,
            message: "Select a campaign first before adding files.",
            severity: "warning",
        });
        return;
    }

    if (attachments.length + files.length > MAX_FILES) {
      setSnackbar({ open: true, message: `You can only upload up to ${MAX_FILES} files.`, severity: "error" });
      return;
    }

    setError("");
    setUploading(true);
    const processedFiles = [];
    for (const file of files) {
      const result = await processFile(file);
      if (result) processedFiles.push(result);
    }
    setAttachments((prev) => [...prev, ...processedFiles]);
    setUploading(false);
  };

  /** Upload to AWS S3 */
  const handleUpload = async () => {
    if (!attachments.length) {
      setSnackbar({ open: true, message: "No files to upload.", severity: "error" });
      return;
    }
    if (!selectedId) {
        setSnackbar({
            open: true,
            message: "Select a campaign first before adding files.",
            severity: "warning",
        });
        return;
    }
    if (attachments.length > MAX_FILES) {
        setSnackbar({
            open: true,
            message: `You can only upload up to ${MAX_FILES} file(s).`,
            severity: "warning",
        });
        return;
    }

    setUploading(true);

    try {

      const uploadedFiles = [];

      for (const file of attachments) {
        // 1. Get signed URL + fileUrl from backend
        const { data } = await getSignedUrlForFile(file, clientId, selectedId);

        const signedUrl = data.signedUrl;
        const fileUrl = data.fileUrl;  // <-- get actual S3 URL
        const key = data.key;          // <-- get S3 key

        if (!signedUrl || !fileUrl) throw new Error("Signed URL or file URL not returned from backend");

        // 2. Upload raw file to S3
        await fetch(signedUrl, {
          method: "PUT",
          body: file.originalFile,
          headers: { "Content-Type": file.type },
        });

        uploadedFiles.push({
          ...file,
          url: fileUrl,   // <-- save real S3 URL
          key,
        });
      }

      const formattedAttachemnt = uploadedFiles.map(f => ({
        name: f.name,
        url: f.url,
        type: f.type,
        size: f.size,
        key: f.key,
      }));

      // PATCH once after all uploads
      await uploadCampaignAttachments(selectedId, formattedAttachemnt);

      setAttachments(uploadedFiles);
      setSnackbar({ open: true, message: "Files uploaded successfully!", severity: "success" });

      //setAttachments([]);
      // Refresh list
        const res = await getCampaignFiles(clientId,selectedId);

        const files = res.data
            ? Array.isArray(res.data)
                ? res.data
                : [res.data] // wrap single object in array
            : [];
        console.log(files)
        const formattedFiles = files.map((file) => ({
            name: file.fileUrl ? file.fileUrl.split("/").pop() : "unknown",
            url: file.fileUrl || "",
            key: file.key || file.fileUrl || "",
            size: file.size || 0,
            preview: file.fileUrl && file.fileUrl.match(/\.(jpg|jpeg|png)$/) ? file.fileUrl : null,
        }));

        setAttachments(formattedFiles);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Upload failed:" + err?.message?.message || "", severity: "error" });
    } finally {
      setUploading(false);
    }
  };

  /** Delete file from S3 */
  const handleDelete = async (key) => {
    if (!key) {
    //  Remove from local attachments state
      const updatedAttachments = attachments.filter(f => f.key !== key);
      setAttachments(updatedAttachments);
      return;
    }

    try {
      // 1. Delete from S3 via backend
        await deleteStorageFile(key);

      // 2. Remove from local attachments state
      const updatedAttachments = attachments.filter(f => f.key !== key);
      setAttachments(updatedAttachments);

      //3. Update campaign with new attachments array
      await deleteCampaignAttachment(selectedId, updatedAttachments);
      //await deleteCampaignAttachment(selectedId, key);

      setSnackbar({ open: true, message: "File deleted successfully", severity: "success" });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Delete failed:" + err?.message?.message || "", severity: "error" });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Typography variant="h5" fontWeight={600}>
        Manage Email Attachments
      </Typography>

      {/* Campaign select */}
      <FormControl fullWidth sx={{ my: 2 }}>
        <Select value={selectedId || ""} onChange={(e) => setSelectedId(e.target.value)} displayEmpty sx={{ borderRadius: 2 }}>
          <MenuItem value="">Select Campaign</MenuItem>
          {campaigns.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
        {!selectedId && <FormHelperText>Campaign wise email attachments</FormHelperText>}
      </FormControl>

      {/* Drag and drop */}
      {selectedId && (
      <Box
        ref={dropRef}
        onDragOver={(e) => { e.preventDefault(); dropRef.current.style.borderColor = "#1976d2"; }}
        onDragLeave={(e) => { e.preventDefault(); dropRef.current.style.borderColor = "grey"; }}
        onDrop={handleDrop}
        sx={{
          border: "2px dashed grey",
          borderRadius: 2,
          p: 6,
          textAlign: "center",
          cursor: "pointer",
          mb: 2,
          "&:hover": { borderColor: "#1976d2" },

        }}
      >
        <CloudUploadIcon sx={{ fontSize: 40, color: "grey.500" }} />
        <Typography variant="body1" sx={{ mt: 1 }}>
          Drag & drop files here or
        </Typography>
        <input
          accept=".pdf,.png,.jpg,.jpeg,.docx"
          style={{ display: "none" }}
          id="upload-button"
          type="file"
          multiple
          onChange={handleFileChange}
        />
        <label htmlFor="upload-button">
          <Button variant="contained" component="span" sx={{ mt: 1 }}>
            Browse
          </Button>
        </label>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Max size: {FILE_SIZE_IN_MB} MB per file | Max {MAX_FILES} file(s) | Allowed: PDF, PNG, JPG, DOCX
        </Typography>
      </Box>
      )}

      {uploading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {attachments.length > 0 && (
        <List>
            {attachments.map((file, index) => (
                <ListItem
                    key={index}
                    secondaryAction={
                        // <IconButton edge="end" onClick={() => handleDelete(file.key || file.name)}>
                        <IconButton edge="end" onClick={() => handleDelete(file.key)}>
                            <DeleteIcon />
                        </IconButton>
                    }
                >
                    {file.preview && (
                        <Avatar
                            src={file.preview}
                            variant="square"
                            sx={{ width: 40, height: 40, mr: 2, cursor: "pointer" }}
                            onClick={() => window.open(file.url, "_blank")}
                        />
                    )}
                    <ListItemText
                        primary={
                            <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: "none", color: "#1976d2" }}
                            >
                                {file.name}
                            </a>
                        }
                        secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    />
                </ListItem>
            ))}
        </List>
      )}
      
      {selectedId && (
      <Button variant="contained" color="primary" 
      onClick={handleUpload} sx={{ mt: 2 }} 
      disabled={uploading || !attachments.length }>
        Upload
      </Button>
      )}

      {!selectedId && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh"}}>
          <Stack direction="column">
            <AttachFileRounded color="primary" sx={{ fontSize: 100, mx: 10, my:1}} />
            <Typography variant="h4" color="textDisabled"> Select Campaign </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
